import { useEffect, useMemo } from 'react';
import { useContextScope, usePostOfficeContext } from '@atlassian/post-office-context';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import {
	createPlacement,
	type PlacementMessageBackendFactory,
	type PlacementMessageFrontendFactory,
} from '@post-office/placement-common-v2';
import type {
	AnyBackendMessageBody,
	BackendPlacementStageInput,
} from '@post-office/placement-common-v2/types';
import type { SerializableRecord } from '@post-office/serializable-record';
import {
	usePlacementGraphQLSubscriptionApi,
	getSubscriptionMessages,
} from '@post-office/use-placement-api/graphql-subscription';
import { filterEligibleMessagesByActiveScopes } from '@post-office/placement-filters/active-scopes-check-filter';
import { usePostOfficeSearchParams } from '@post-office/use-post-office-search-params';
import { scopes } from './scopes';
import { filterExperimentalMessages } from './experimental-message-list';

import { selectRovoNudgeMessages } from './select-messages';
import { defaultBackendPlacementStages } from '@post-office/placement-common-v2/placement-stages';
import {
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type DefaultPlacementBackend,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

const ROVO_NUDGE_PLACEMENT_ID = 'rovo-nudge';

const LIVE_NUDGE_DEMO_MESSAGE_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData {
	__typename
	title
	description
}`;

const LIVE_AND_PERSISTED_TEST_MESSAGE_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData {
	__typename
	title
	description
}`;

const JIRA_BOARD_CATCH_UP_ROVO_NUDGE_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData {
	__typename
	title
	description
}`;

const JIRA_BOARD_BACKLOG_UPDATE_WORK_SUGGESTIONS_DATA_FRAGMENT = `... on PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData {
	__typename
	conversationChannelId
	numberOfSuggestions
}`;

type PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData = {
	__typename: 'PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData';
	title: string;
	description: string;
};

type PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData = {
	__typename: 'PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData';
	title: string;
	description: string;
};

type PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData = {
	__typename: 'PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData';
	title: string;
	description: string;
};

type PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData = {
	__typename: 'PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData';
	conversationChannelId: string;
	numberOfSuggestions: number;
};

type SubscriptionMessageData =
	| PostOfficeMessageRovoNudgeLiveNudgeDemoMessageData
	| PostOfficeMessageRovoNudgeLiveAndPersistedTestMessageData
	| PostOfficeMessageRovoNudgeJiraBoardCatchUpRovoNudgeData
	| PostOfficeMessageRovoNudgeJiraBoardBacklogUpdateWorkSuggestionsData;

type RovoNudgeSubscriptionVariables = {
	activeScopes: string;
	pathname?: string;
	context?: {
		tenantId?: string;
		orgId?: string;
		locale?: string;
		subproduct?: string;
		isSiteAdmin?: boolean;
		productEdition?: string;
		product?: string;
	};
};

const doesScopeMatchPathname = (scopeName: string, pathname?: string): boolean => {
	if (!pathname) {
		return false;
	}

	const scope = scopes.find((scopeConfig) => scopeConfig.name === scopeName);
	const pathnameMatcher = scope?.when?.route?.pathname as
		| string
		| { equals?: string; matches?: string }
		| undefined;

	if (!pathnameMatcher) {
		return true;
	}
	if (typeof pathnameMatcher === 'string') {
		return pathname === pathnameMatcher;
	}
	if (typeof pathnameMatcher.equals === 'string') {
		return pathname === pathnameMatcher.equals;
	}
	if (typeof pathnameMatcher.matches === 'string') {
		try {
			return new RegExp(pathnameMatcher.matches).test(pathname);
		} catch {
			return false;
		}
	}
	return true;
};
const placement = createPlacement({
	placementId: ROVO_NUDGE_PLACEMENT_ID,
	options: {
		choreographer: {
			isDefaultEnabled: true, // change this to true when rolling out to production
		},
		hideInIsolatedCloud: false,
		subscription: {
			enabled: true,
		},
	},
});
export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;
export const createBackend: (
	placementBackendParams?:
		| DefaultPlacementBackend<{
				transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
				hydrate?: AnyBackendMessageStage;
		  }>
		| undefined,
) => PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = placement.createBackend;

/**
 * preInit stage that applies the active scopes check filter.
 *
 * rovo-nudge is an in-app-v2 placement. The in-app-v2 runtime does not expose
 * a direct `pre-filters` injection pathway (unlike in-app / screen-space-flags which
 * passes `pre-filters` through `getEligibleMessages` additionalParameters.stages).
 *
 * Using `preInit` is the recommended approach for in-app-v2 placements:
 * - It executes before `init` (transformMetadata) and `postInit` (hydrate),
 *   so filtered messages are never unnecessarily hydrated.
 * - `activeScopes` is forwarded by the placement router into
 *   `request.additionalContext.activeScopes`, making it available here.
 * - The scope-check logic is identical to what screen-space-flags uses via pre-filters;
 *   the `createActiveScopesCheckFilter` export from `@post-office/placement-filters`
 *   is the shared, extracted version of that filter.
 */
const activeScopesPreInitStage = async (
	input: BackendPlacementStageInput<SerializableRecord>,
): Promise<{ messages: typeof input.data.messages }> => {
	const { request, data } = input;
	const activeScopes = request.additionalContext?.activeScopes;

	return {
		messages: filterEligibleMessagesByActiveScopes(data.messages, {
			activeScopes,
			requestContext: request,
		}),
	};
};

const RovoNudgeSubscriptionContent = ({
	Messages,
	variables,
}: {
	Messages: { render: AnyFrontendStage };
	variables: RovoNudgeSubscriptionVariables;
}) => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	// Register this effect before the subscription hook so the placement traffic event is
	// scheduled as close as possible to subscription setup. The event is intended to track
	// every aligned rovo-nudge subscription attempt; placing it after the subscription hook
	// can undercount when subscription setup effects run before this analytics effect.
	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'rendered',
			actionSubject: 'rovoNudgePlacement',
			attributes: {
				placementId: ROVO_NUDGE_PLACEMENT_ID,
				activeScope: variables.activeScopes,
				...(variables.context?.locale ? { locale: variables.context.locale } : {}),
				...(variables.context?.subproduct ? { subproduct: variables.context.subproduct } : {}),
				...(variables.context?.product ? { product: variables.context.product } : {}),
			},
		});
	}, [fireAnalyticsEvent, variables]);

	const { data: subscriptionData } = usePlacementGraphQLSubscriptionApi<SubscriptionMessageData>({
		dataFragments: [
			LIVE_NUDGE_DEMO_MESSAGE_DATA_FRAGMENT,
			LIVE_AND_PERSISTED_TEST_MESSAGE_DATA_FRAGMENT,
			JIRA_BOARD_CATCH_UP_ROVO_NUDGE_DATA_FRAGMENT,
			JIRA_BOARD_BACKLOG_UPDATE_WORK_SUGGESTIONS_DATA_FRAGMENT,
		],
		variables,
	});

	const messages = useMemo(
		() => getSubscriptionMessages<SubscriptionMessageData>(subscriptionData),
		[subscriptionData],
	);
	// The backend is responsible for ranking/sorting rovo-nudge messages.
	// The placement intentionally renders only the highest-priority message,
	// and the rendered message template owns the full nudge lifecycle via NudgeDefinition.
	// Experimental messages are filtered out if the user is not in the experiment cohort.
	const topMessage = filterExperimentalMessages(messages)[0];

	if (!topMessage?.data) {
		return null;
	}

	const { data, ...message } = topMessage;
	return <Messages.render key={message.messageInstanceId} {...message} {...data} />;
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const activeScopes = useContextScope({
		scopes,
		treatIntersectionAsSame: true,
	});

	const activeScopeName = activeScopes.current[0]?.name;

	const { pathname } = usePostOfficeSearchParams();
	const { tenantId, orgId, locale, subproduct, isSiteAdmin, productEdition, product } =
		usePostOfficeContext();

	const isScopePathnameAligned = useMemo(
		() => (activeScopeName ? doesScopeMatchPathname(activeScopeName, pathname) : false),
		[activeScopeName, pathname],
	);

	const variables = useMemo<RovoNudgeSubscriptionVariables | null>(
		() =>
			activeScopeName && isScopePathnameAligned
				? {
						activeScopes: activeScopeName,
						pathname,
						context: {
							tenantId,
							orgId,
							locale,
							subproduct,
							isSiteAdmin,
							productEdition,
							product,
						},
					}
				: null,
		[
			activeScopeName,
			isScopePathnameAligned,
			pathname,
			tenantId,
			orgId,
			locale,
			subproduct,
			isSiteAdmin,
			productEdition,
			product,
		],
	);

	if (!variables) {
		return null;
	}

	return <RovoNudgeSubscriptionContent Messages={Messages} variables={variables} />;
});

export const PlacementBackend: PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = createBackend({
	/**
	 * Override the default placement stages to add a postInit stage that selects
	 * the highest-priority messages for rovo-nudge:
	 * - Max 1 live subscription message (latest by eventTime; subscriptionConfig.deliveryMechanism is live-only or live-and-persisted)
	 * - Max 1 other message (latest by eventTime)
	 * - Live subscription message is prioritized first
	 *
	 * `subscriptionConfig` is copied by stripUnserializablePayload from messageConfig.subscriptionConfig.
	 */
	stages: (placementMessageStages) => {
		const { init, postInit } = defaultBackendPlacementStages(placementMessageStages);
		return {
			preInit: activeScopesPreInitStage,
			init,
			postInit: async (input: BackendPlacementStageInput<AnyBackendMessageBody>) => {
				const hydratedData = postInit ? await postInit(input) : input.data;
				return {
					...hydratedData,
					messages: selectRovoNudgeMessages(hydratedData.messages),
				};
			},
		};
	},
});
